import { useContext } from 'react';
import { Box } from '@givebutter/primitives/box';
import { Label } from '@givebutter/primitives/label';
import { faAddressBook } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContactTableCustomFields } from 'assets/js/pages/contacts/hooks/useContactTableCustomFields';
import { ContactTableKey, transformTableKeyToReadable } from 'assets/js/pages/contacts/transform';
import { FilterBarContainer } from 'components/filter/filters-next/FilterBarContainer';
import { FilterGroupBar } from 'components/filter/filters-next/FilterGroupBar';
import { SaveNewSegmentButton } from 'components/filter/filters-next/SaveNewSegmentButton';
import { SegmentsIndexDrawer } from 'components/filter/filters-next/SegmentIndexDrawer';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { TableContext } from 'components/table/TableContext';
import { TableCoreStateContext } from 'components/table/TableCoreStateContext';
import { ResourceTableProps } from 'components/table/types';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useTableSchema } from 'customHooks/useTableSchema';
import { truncateText } from 'helpers/formatting';
import { CustomField } from 'types/domain';

const CUSTOM_FIELD_TRUNCATE_LIMIT = 30;

const CompaniesTable = (props: ResourceTableProps) => {
    const { children, enableFilters = true, enableReset = true } = props;
    const { enableSchema, search, setSearch } = useContext(TableCoreStateContext);
    const {
        data: { columns: columnSchema },
    } = useTableSchema('companies');
    const { table } = useContext(TableContext);

    const contactCustomFields = useContactTableCustomFields();

    return (
        <Box className="flex flex-col overflow-hidden">
            <div className={'flex flex-col'}>
                <div
                    className={
                        'flex flex-wrap items-center justify-between gap-4 border-b border-b-gray-100 p-4 md:flex-nowrap'
                    }
                >
                    <Label className="sr-only" htmlFor="contacts_search">
                        Search
                    </Label>
                    <Input.Root size={'sm'}>
                        <Input.SearchIcon />
                        <Input.Form
                            id="contacts_search"
                            value={search}
                            placeholder={'Search'}
                            onChange={e => setSearch?.(e.target.value)}
                        />
                    </Input.Root>
                    <div className="flex flex-wrap items-center gap-2.5">
                        <Table.ResultsCount />
                        <div className={'flex flex-wrap gap-1'}>
                            {enableFilters && <SaveNewSegmentButton />}
                            {enableFilters && (
                                <SegmentsIndexDrawer
                                    buttonProps={{
                                        ['data-intercom-target']:
                                            IntercomProductTourTargets.ContactsSegments,
                                    }}
                                />
                            )}
                            {table.options.enableHiding && (
                                <Table.ColumnVisibility
                                    transformIdToReadable={key => {
                                        if (key.includes('cf_')) {
                                            const [, fieldId] = key.split('_');
                                            const field = contactCustomFields.find(
                                                cf => cf.id === Number(fieldId),
                                            ) as CustomField;
                                            return truncateText(
                                                field.title,
                                                CUSTOM_FIELD_TRUNCATE_LIMIT,
                                            );
                                        }

                                        const calculatedColumn = columnSchema.find(
                                            col => col.name === key,
                                        );
                                        if (enableSchema && calculatedColumn) {
                                            return calculatedColumn.label;
                                        }

                                        return transformTableKeyToReadable(key as ContactTableKey);
                                    }}
                                />
                            )}
                            <Table.ToggleFullWidth />
                            {enableReset && <Table.Reset />}
                        </div>
                    </div>
                </div>
                {enableFilters && (
                    <FilterBarContainer>
                        <FilterGroupBar
                            data-intercom-target={IntercomProductTourTargets.ContactsFilters}
                            quickFilters={[
                                'total_contributions',
                                'recurring_contributions',
                                'created_at',
                                'tags',
                            ]}
                        />
                    </FilterBarContainer>
                )}
            </div>
            <Table.FiltersBar />
            <Table.Filters />
            <Table.Content>
                <Table.Header />
                <Table.Body />
            </Table.Content>
            <Table.Empty variant={'primary'}>
                <Table.EmptyIcon>
                    <FontAwesomeIcon icon={faAddressBook} />
                </Table.EmptyIcon>
                <Table.EmptyTitle>No companies found</Table.EmptyTitle>
                <Table.EmptyWhenNotSearching>{children}</Table.EmptyWhenNotSearching>
            </Table.Empty>
            <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                <Table.PaginationPageNavigation />
                <Table.PaginationPerPageDropdown />
            </Table.Pagination>
        </Box>
    );
};

export { CompaniesTable };
